import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { hot } from 'react-hot-loader';

import {
    ApplicationErrorBoundary,
    SearchFilter,
    SearchResults,
    SearchFilterModal,
    FilterCategoriesModal,
    DiscoverPagination,
    ScrollToTopButton,
    QuickView,
    DialogModal
} from 'components';
import CONSTANTS from 'components/search-results/constants/constants';
import searchActions from 'state/actions/search';
import 'components/search-results/search-results.scss';
import SearchResultsHeader from 'components/search-results/header/search-results-header';
import PAGE_TYPES from 'constants/page-types';
import ENDPOINTS from 'constants/endpoints';
import STRINGS from 'constants/strings';
import AdsenseShopping from 'static/script/app/ads/adsense-shopping';
import Adsense from 'static/script/app/ads/adsense';
import { composeAdsenseChannel } from 'static/script/app/ui/adsense-utils';
import * as gtm from 'components/search-results/search-gtm';
import {
    stripPaginationFromQueryParameters,
    toAbsoluteUrl,
    toSearchString
} from 'utils/url';
import {
    addOrRemoveBrand,
    createBrandFilterString
} from './search-results-page-logic';
import FoldedCornerContainer from '../components/search-results/folded-corner-container.jsx';

class SearchResultsPage extends Component {
    static propTypes = {
        initData: PropTypes.object.isRequired,
        gpt: PropTypes.object.isRequired,
        queryParams: PropTypes.object.isRequired,
        pageType: PropTypes.string.isRequired,
        searchResults: PropTypes.object,
        fetching: PropTypes.string,
        selectedCategory: PropTypes.object,
        itemLayout: PropTypes.string,
        adSenseQuery: PropTypes.string,
        adSenseShoppingPublisherId: PropTypes.string,
        canonicalLink: PropTypes.string,
        metaTitle: PropTypes.string,
        metaDescription: PropTypes.string,
        breadcrumbs: PropTypes.string,
        dispatch: PropTypes.func.isRequired,
        history: PropTypes.object,
        location: PropTypes.object
    };
    constructor(props) {
        super(props);
        this.state = {
            modalOpen: null
        };
    }

    search = () => {
        const {
            dispatch,
            queryParams,
            pageType,
            location: { pathname }
        } = this.props;
        if (!queryParams) {
            return;
        }
        const endpoint =
            pageType === PAGE_TYPES.CATEGORY_RESULT_PAGE
                ? pathname
                : ENDPOINTS.SEARCH;
        dispatch(searchActions.fetchSearchResults(endpoint, queryParams));
    };

    componentWillUnmount() {}

    componentDidMount() {
        const { dispatch, history } = this.props;
        dispatch(searchActions.updateRouting(history));
        this.registerApp();
    }

    componentDidUpdate(prevProps) {
        //this ensures we catch changing between browse & search
        if (prevProps.pageType !== this.props.pageType) {
            this.search();
        }
    }

    registerApp = () => {
        const { dispatch, history } = this.props;
        history.listen(() => {
            dispatch(searchActions.historyLocationDidUpdate(history));
        });
    };

    pushToHistory = query => {
        const { history, pageType, searchResults } = this.props;
        const { filterCategories = {} } = searchResults;
        const { activeCategory = {} } = filterCategories;
        const { url: categoryUrl } = activeCategory;

        let pathname;
        if (pageType === PAGE_TYPES.CATEGORY_RESULT_PAGE) {
            pathname = categoryUrl; // ADD NEW LINK PROPERTY THAT WILL BE IN THE TREE
            delete query.categoryId;
        } else {
            pathname = 'search';
        }
        history.push({
            pathname: pathname,
            search: toSearchString(query)
        });
    };

    updateHistory = param => {
        const { queryParams } = this.props;
        this.pushToHistory({
            ...stripPaginationFromQueryParameters(queryParams),
            ...param
        });
    };

    goToBLP = () => {
        const { queryParams } = this.props;
        const path = toSearchString(
            stripPaginationFromQueryParameters(queryParams)
        );
        window.location = '/skor-1623' + path;
    };

    handleClearAllFilters = () => {
        const { queryParams } = this.props;
        const query = {
            q: queryParams.q
        };

        this.pushToHistory(query);
    };

    handleCategoryChange = value => {
        const { dispatch } = this.props;
        if (value.id === 1623) {
            this.goToBLP();
        } else {
            this.updateHistory({ categoryId: value.id });
            dispatch(searchActions.setActiveCategory(value));
        }
    };

    handleFilterChange = (filterType, value) => {
        const { dispatch } = this.props;
        this.updateHistory({ [filterType]: value });
        dispatch(searchActions.setSingleFilterValue(filterType, value));
    };

    handlePriceFilterChange = ({ fromPrice, toPrice }) => {
        this.updateHistory({
            ['fromPrice']: fromPrice,
            ['toPrice']: toPrice
        });
    };

    handleBrandChange = (filterType, value) => {
        const {
            searchResults: {
                filterBrands: { selectedValues }
            }
        } = this.props;

        const query = createBrandFilterString(
            addOrRemoveBrand(selectedValues, value)
        );

        gtm.trackFilter(filterType, value);
        this.updateHistory({ [filterType]: query });
    };

    handleModalClick = value => {
        this.setState(prevState => ({
            ...prevState,
            modalOpen: value
        }));
    };

    resolveHeaderCategory() {
        const {
            selectedCategory = {},
            categories,
            searchResults,
            queryParams
        } = this.props;
        const { filterCategories = {}, introText } = searchResults;
        const { activeCategory = {}, categoryTree } = filterCategories;
        const headerCategory = {
            categoryName:
                selectedCategory.name ||
                activeCategory.name ||
                categoryTree.name,
            categoryId:
                selectedCategory.id || activeCategory.id || categoryTree.id,
            introText: introText
        };
        if (!headerCategory.categoryName && queryParams.categoryId) {
            const category =
                categories.find(
                    c => c.id === parseInt(queryParams.categoryId)
                ) || {};
            headerCategory.categoryName = category.name;
            headerCategory.categoryId = category.id;
        }
        return headerCategory;
    }

    render() {
        const {
            initData,
            gpt,
            queryParams,
            searchResults,
            fetching,
            itemLayout,
            pageType,
            dispatch,
            adSenseQuery,
            adSenseShoppingPublisherId,
            mainText,
            canonicalLink,
            metaTitle,
            metaDescription,
            breadcrumbs,
            items
        } = this.props;
        const {
            filterCategories = {},
            filters = {},
            filterBrands = {},
            filterPrice,
            pagination,
            totalItems,
            activeFilters
        } = searchResults;
        const filtersLoading =
            (!filters || Object.entries(filters).length < 1) &&
            fetching === 'fetching';
        const filtersUpdating = fetching === 'fetching';
        const { categoryTree = {} } = filterCategories;
        const headerCategory = this.resolveHeaderCategory();
        const categoryResultPage = pageType === PAGE_TYPES.CATEGORY_RESULT_PAGE;
        const adsEnabled =
            initData.featureSwitches &&
            initData.featureSwitches['google-ads'] === true;

        let gaModalLabel = '';
        if (this.state.modalOpen === CONSTANTS.MODALS.FILTER_MODAL) {
            gaModalLabel = 'Filter menu';
        } else if (this.state.modalOpen === CONSTANTS.MODALS.CATEGORY_MODAL) {
            gaModalLabel = 'Category menu';
        }

        delete queryParams.fe; // Remove workaround queryparameter
        return (
            <div className="search-results-page">
                <Helmet>
                    <title>{metaTitle}</title>
                    <meta name="description" content={metaDescription} />
                    {canonicalLink && (
                        <link
                            rel="canonical"
                            href={toAbsoluteUrl(canonicalLink)}
                        />
                    )}
                    <meta
                        property="og:site_name"
                        content={STRINGS.META.DEFAULT_SITE_NAME}
                    />
                    <meta property="og:url" content={window.location.href} />
                    <meta
                        property="og:title"
                        content={
                            categoryResultPage
                                ? metaTitle
                                : `"${queryParams.q}" - Tradera.com`
                        }
                    />
                    <meta
                        property="og:description"
                        content={
                            metaDescription || STRINGS.META.DEFAULT_DESCRIPTION
                        }
                    />
                    <meta
                        property="og:image"
                        content="http://static.tradera.net/touchweb/static/output/images/open-graph-default.ee86259e.jpg"
                    />
                    <meta
                        property="og:image:secure_url"
                        content="https://static.tradera.net/touchweb/static/output/images/open-graph-default.ee86259e.jpg"
                    />
                    {breadcrumbs && (
                        <script type="application/ld+json">
                            {breadcrumbs}
                        </script>
                    )}
                </Helmet>
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-3 d-none d-md-block mr-xl-3 search-filter-wrapper">
                            {filtersLoading ? (
                                <div className="filters--loading" />
                            ) : (
                                <SearchFilter
                                    filterCategories={filterCategories}
                                    filterBrands={filterBrands}
                                    filterPrice={filterPrice}
                                    filters={filters}
                                    handleCategoryChange={
                                        this.handleCategoryChange
                                    }
                                    handleFilterChange={(filterType, value) => {
                                        gtm.trackFilter(filterType, value);
                                        this.handleFilterChange(
                                            filterType,
                                            value
                                        );
                                    }}
                                    handleBrandChange={this.handleBrandChange}
                                    handlePriceFilterChange={priceFilter => {
                                        gtm.trackPriceFilter(
                                            priceFilter.fromPrice,
                                            priceFilter.toPrice
                                        );
                                        this.handlePriceFilterChange(
                                            priceFilter
                                        );
                                    }}
                                    pageType={pageType}
                                    queryParams={queryParams}
                                    isUpdating={filtersUpdating}
                                />
                            )}
                        </div>
                        <div className="col-md-9 col-sm-12 search-results-wrapper">
                            <SearchResultsHeader
                                queryParams={queryParams}
                                itemsFound={totalItems}
                                headerCategory={headerCategory}
                                filters={filters}
                                filterBrands={filterBrands}
                                filterPrice={filterPrice}
                                handleBrandChange={this.handleBrandChange}
                                handleFilterChange={this.handleFilterChange}
                                handlePriceFilterChange={
                                    this.handlePriceFilterChange
                                }
                                activeFilters={activeFilters}
                                handleModalClick={this.handleModalClick} // Replace with portal in header?
                                handleClearAllFilters={
                                    this.handleClearAllFilters
                                }
                                itemLayout={itemLayout}
                                pageType={pageType}
                                initData={initData}
                                dispatch={dispatch}
                            />
                            <SearchResults
                                gptData={gpt}
                                queryParams={queryParams}
                                searchResults={searchResults}
                                fetching={fetching === 'fetching'}
                                isLoggedIn={initData.isLoggedIn}
                                itemLayout={itemLayout}
                                items={items}
                                pageIndex={
                                    pagination ? pagination.pageIndex : null
                                }
                                pageType={pageType}
                                adsEnabled={adsEnabled}
                                dispatch={dispatch}
                            />
                            <DiscoverPagination
                                pagination={pagination}
                                queryParams={queryParams}
                            />
                            <ScrollToTopButton
                                onClick={() =>
                                    gtm.trackNavigation('Back to top')
                                }
                            />
                            {mainText ? (
                                <FoldedCornerContainer html={mainText} />
                            ) : null}
                            {adSenseQuery && adSenseShoppingPublisherId ? (
                                <ApplicationErrorBoundary>
                                    <AdsenseShopping
                                        publisherId={adSenseShoppingPublisherId}
                                        searchQuery={adSenseQuery}
                                        className="mt-4"
                                    />
                                </ApplicationErrorBoundary>
                            ) : null}

                            {adSenseQuery &&
                            typeof pagination !== 'undefined' ? (
                                <ApplicationErrorBoundary>
                                    <Adsense
                                        searchQuery={adSenseQuery}
                                        channel={composeAdsenseChannel(
                                            pageType,
                                            [categoryTree.name]
                                        )}
                                        pageIndex={pagination.pageIndex}
                                        className="mt-5"
                                    />
                                </ApplicationErrorBoundary>
                            ) : null}
                        </div>
                    </div>
                </div>
                {this.state.modalOpen && (
                    <DialogModal mobile>
                        <div className="search-modal bg-white d-flex flex-column">
                            {this.state.modalOpen ===
                                CONSTANTS.MODALS.CATEGORY_MODAL && (
                                <FilterCategoriesModal
                                    filterCategories={filterCategories}
                                    handleCategoryChange={
                                        this.handleCategoryChange
                                    }
                                    handleModalClick={this.handleModalClick}
                                    pageType={pageType}
                                    isUpdating={filtersUpdating}
                                    queryParams={queryParams}
                                />
                            )}
                            {this.state.modalOpen ===
                                CONSTANTS.MODALS.FILTER_MODAL && (
                                <SearchFilterModal
                                    searchResults={searchResults}
                                    handleFilterChange={this.handleFilterChange}
                                    handleBrandChange={this.handleBrandChange}
                                    handlePriceFilterChange={
                                        this.handlePriceFilterChange
                                    }
                                    handleClearAllFilters={
                                        this.handleClearAllFilters
                                    }
                                    handleModalClick={this.handleModalClick}
                                    itemLayout={itemLayout}
                                    dispatch={dispatch}
                                    queryParams={queryParams}
                                    pageType={pageType}
                                    isUpdating={filtersUpdating}
                                />
                            )}
                            <div
                                className="p-3 fixed-bottom bg-white btn-group"
                                role="group">
                                <button
                                    className="btn btn-fluid btn-primary"
                                    onClick={() => {
                                        gtm.trackAction(gaModalLabel, 'Close');
                                        this.handleModalClick('');
                                    }}>
                                    Stäng
                                </button>
                                <button
                                    className="btn btn-fluid btn-primary"
                                    onClick={() => {
                                        gtm.trackAction(
                                            gaModalLabel,
                                            'View items'
                                        );
                                        this.handleModalClick('');
                                    }}>
                                    Visa annonser
                                </button>
                            </div>
                        </div>
                    </DialogModal>
                )}
                <div id="filter-portal" />
                <QuickView />
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const searchResult = state.search;
    const getItemsById = state.search.itemsById.map(id => state.items[id]);

    return {
        initData: state.initData,
        searchResults: searchResult,
        items: getItemsById,
        fetching: state.search.isFetching,
        mainText: searchResult ? searchResult.mainText : null,
        routing: state.search.routing,
        selectedCategory: state.search.activeCategory,
        itemLayout: state.ui.itemLayout,
        canonicalLink: state.page.canonicalLink,
        metaTitle: state.page.metaTitle,
        metaDescription: state.page.metaDescription,
        breadcrumbs: state.page.breadcrumbs,
        gpt: state.ads.gpt,
        adSenseQuery: state.ads.adSenseQuery,
        adSenseShoppingPublisherId: state.ads.adSenseShoppingPublisherId,
        categories: state.page.allCategories,
        ...ownProps
    };
};

export default hot(module)(
    withRouter(connect(mapStateToProps)(SearchResultsPage))
);



// WEBPACK FOOTER //
// ./src/EbaySweden.TouchWeb/static/script/containers/search-results-page.jsx